import { useState } from 'react';
import {
  Stack,
  Paper,
  TextInput,
  Textarea,
  NumberInput,
  Button,
  Group,
  Text,
  Alert,
  FileInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCurrencyDollar, IconCheck, IconUpload } from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import { useCreateSupportTicket, useUploadTicketAttachment } from '@/hooks/usePortalApi';

const ACCEPTED_TYPES = '.pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv';

export default function PriceMatch() {
  const [productName, setProductName] = useState('');
  const [competitorName, setCompetitorName] = useState('');
  const [competitorPrice, setCompetitorPrice] = useState<number | ''>('');
  const [details, setDetails] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const createTicket = useCreateSupportTicket();
  const uploadAttachment = useUploadTicketAttachment();

  const canSubmit = productName.trim() && competitorPrice && files.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);

    const description = [
      `Product: ${productName.trim()}`,
      competitorName ? `Competitor: ${competitorName.trim()}` : null,
      `Competitor Price: $${Number(competitorPrice).toFixed(2)}`,
      `Supporting Evidence: ${files.length} file(s) attached`,
      details.trim() ? `\nAdditional Details:\n${details.trim()}` : null,
    ]
      .filter(Boolean)
      .join('\n');

    createTicket.mutate(
      {
        subject: `Price Match Request: ${productName.trim()}`,
        category: 'billing',
        priority: 'medium',
        description,
      },
      {
        onSuccess: async (response) => {
          const ticketId = (response as any)?.data?.id;

          // Upload all files as attachments
          if (ticketId) {
            for (const file of files) {
              const formData = new FormData();
              formData.append('file', file);
              try {
                await uploadAttachment.mutateAsync({ id: ticketId, formData });
              } catch {
                // Non-blocking — ticket is already created
              }
            }
          }

          setSubmitted(true);
          setProductName('');
          setCompetitorName('');
          setCompetitorPrice('');
          setDetails('');
          setFiles([]);
          setSubmitting(false);
          notifications.show({
            title: 'Request Submitted',
            message: 'Your price match request has been submitted for review.',
            color: 'green',
          });
        },
        onError: () => {
          setSubmitting(false);
          notifications.show({
            title: 'Error',
            message: 'Failed to submit request. Please try again.',
            color: 'red',
          });
        },
      },
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Price Match Request"
        description="Found a better price? Submit a request with supporting evidence and our team will review it."
      />

      {submitted && (
        <Alert icon={<IconCheck size={16} />} color="green" withCloseButton onClose={() => setSubmitted(false)}>
          Your price match request has been submitted. Our pricing team will review it and get back to you, typically within 1-2 business days.
        </Alert>
      )}

      <Paper p="lg" withBorder>
        <Stack gap="md" maw={500}>
          <Text size="sm" c="dimmed">
            Tell us about the product and the price you found. Please attach supporting evidence (competitor price list, screenshot, quote, etc.).
          </Text>

          <TextInput
            label="Product Name or SKU"
            placeholder="e.g. Organic Romaine Hearts"
            value={productName}
            onChange={(e) => setProductName(e.currentTarget.value)}
            required
          />

          <NumberInput
            label="Competitor Price"
            placeholder="0.00"
            leftSection={<IconCurrencyDollar size={16} />}
            value={competitorPrice}
            onChange={(v) => setCompetitorPrice(typeof v === 'number' ? v : '')}
            min={0}
            decimalScale={2}
            required
          />

          <TextInput
            label="Competitor Name"
            placeholder="Optional"
            value={competitorName}
            onChange={(e) => setCompetitorName(e.currentTarget.value)}
          />

          <FileInput
            label="Supporting Evidence"
            description="Upload a price list, screenshot, or quote"
            placeholder="Choose files..."
            leftSection={<IconUpload size={16} />}
            accept={ACCEPTED_TYPES}
            multiple
            value={files}
            onChange={(v) => setFiles(v ?? [])}
            required
          />

          <Textarea
            label="Additional Details"
            placeholder="Volume you'd like to order, delivery frequency, etc."
            value={details}
            onChange={(e) => setDetails(e.currentTarget.value)}
            minRows={3}
          />

          <Group justify="flex-end">
            <Button
              onClick={handleSubmit}
              loading={submitting}
              disabled={!canSubmit}
            >
              Submit Request
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
